"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ENS = exports.FDSNameResolverContract = exports.FDSReverseRegistrarContract = exports.FDSRegistrarContract = exports.PublicResolverContract = exports.ENSRegistryContract = void 0;
const ethers_1 = require("ethers");
const constants_1 = require("../constants/constants");
const environment_1 = require("../constants/environment");
const tx_1 = require("../utils/tx");
const keys_1 = require("../utils/keys");
const environments_enum_1 = require("../model/environments.enum");
const ENSRegistry_json_1 = __importDefault(require("../contracts/ENSRegistry/ENSRegistry.json"));
const PublicResolver_json_1 = __importDefault(require("../contracts/PublicResolver/PublicResolver.json"));
const FDSRegistrar_json_1 = __importDefault(require("../contracts/FDSRegistrar/FDSRegistrar.json"));
const FDSReverseRegistrar_json_1 = __importDefault(require("../contracts/FDSReverseRegistrar/FDSReverseRegistrar.json"));
const FDSNameResolver_json_1 = __importDefault(require("../contracts/FDSNameResolver/FDSNameResolver.json"));
const domains_1 = require("../utils/domains");
const blockchain_1 = require("../utils/blockchain");
const address_1 = require("../utils/address");
const { keccak256, toUtf8Bytes, namehash } = ethers_1.utils;
exports.ENSRegistryContract = ENSRegistry_json_1.default;
exports.PublicResolverContract = PublicResolver_json_1.default;
exports.FDSRegistrarContract = FDSRegistrar_json_1.default;
exports.FDSReverseRegistrarContract = FDSReverseRegistrar_json_1.default;
exports.FDSNameResolverContract = FDSNameResolver_json_1.default;
var RegisterUsernameStage;
(function (RegisterUsernameStage) {
    RegisterUsernameStage[RegisterUsernameStage["FDS_REGISTER_COMPLETED"] = 1] = "FDS_REGISTER_COMPLETED";
    RegisterUsernameStage[RegisterUsernameStage["SET_RESOLVER_COMPLETED"] = 2] = "SET_RESOLVER_COMPLETED";
    RegisterUsernameStage[RegisterUsernameStage["SET_PUBLIC_KEY_COMPLETED"] = 3] = "SET_PUBLIC_KEY_COMPLETED";
    RegisterUsernameStage[RegisterUsernameStage["SET_NAME_COMPLETED"] = 4] = "SET_NAME_COMPLETED";
})(RegisterUsernameStage || (RegisterUsernameStage = {}));
/**
 * ENS Class
 * Provides interface for interaction with the ENS smart contracts
 */
class ENS {
    constructor(config = environment_1.ENS_ENVIRONMENT_CONFIGS[environments_enum_1.Environments.LOCALHOST], signerOrProvider = null, domain = constants_1.ENS_DOMAIN, connectionConfig) {
        this.config = config;
        this.domain = domain;
        this._provider = new ethers_1.providers.JsonRpcProvider(connectionConfig || config.rpcUrl);
        const { ensRegistry, fdsRegistrar, publicResolver, reverseResolver, nameResolver } = config.contractAddresses;
        this._ensRegistryContract = new ethers_1.Contract(ensRegistry, exports.ENSRegistryContract.abi, this._provider);
        this._publicResolverContract = new ethers_1.Contract(publicResolver, exports.PublicResolverContract.abi, this._provider);
        this._fdsRegistrarContract = new ethers_1.Contract(fdsRegistrar, exports.FDSRegistrarContract.abi, this._provider);
        this._reverseRegistrarContract = new ethers_1.Contract(reverseResolver, exports.FDSReverseRegistrarContract.abi, this._provider);
        this._nameResolverContract = new ethers_1.Contract(nameResolver, exports.FDSNameResolverContract.abi, this._provider);
        if (signerOrProvider) {
            this.connect(signerOrProvider);
        }
    }
    /**
     * returns RPC provider
     */
    get provider() {
        return this._provider;
    }
    /**
     * Connects signer to the smart contracts
     * @param signerOrProvider An instance of ethers.js Wallet or any other signer
     */
    connect(signerOrProvider) {
        this._publicResolverContract = this._publicResolverContract.connect(signerOrProvider);
        this._fdsRegistrarContract = this._fdsRegistrarContract.connect(signerOrProvider);
        this._ensRegistryContract = this._ensRegistryContract.connect(signerOrProvider);
        this._reverseRegistrarContract = this._reverseRegistrarContract.connect(signerOrProvider);
        this._nameResolverContract = this._nameResolverContract.connect(signerOrProvider);
    }
    /**
     * Returns owner address of the provided username on ENS
     * @param username ENS username
     * @returns owner's address
     */
    getUsernameOwner(username) {
        (0, domains_1.assertUsername)(username);
        const usernameHash = this.hashUsername(username);
        return this._ensRegistryContract.owner(usernameHash);
    }
    /**
     * Checks whether username is available on ENS
     * @param username ENS username
     * @returns True if the username is available
     */
    isUsernameAvailable(username) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, domains_1.assertUsername)(username);
            const owner = yield this.getUsernameOwner(username);
            return owner === constants_1.NULL_ADDRESS;
        });
    }
    /**
     * Creates a request object that needs to be used to invoke the registerUsername method.
     * The request object can be used to invoke the method multiple times without making any
     * inconsistencies. This can be useful if registration fails because of insufficient funds,
     * or some other reason.
     * @param username ENS username
     * @param address Owner address of the username
     * @param publicKey Hex string of a public key
     * @param expires Time in seconds
     * @returns ServiceRequest instance that can be used to invoke the request multiple times if fails.
     */
    createRegisterUsernameRequest(username, address, publicKey, expires = 86400) {
        return {
            stage: 0,
            data: {
                username,
                address,
                publicKey,
                expires,
            },
            completedTxs: [],
        };
    }
    /**
     * Sets owner of the provided username on ENS
     * @param registerRequest request object previously created by the createRegisterUsernameRequest method
     */
    registerUsername(registerRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { username, address, publicKey, expires }, } = registerRequest;
            try {
                if (registerRequest.stage < RegisterUsernameStage.FDS_REGISTER_COMPLETED) {
                    (0, domains_1.assertUsername)(username);
                    let ownerAddress = constants_1.NULL_ADDRESS;
                    if (this.config.performChecks) {
                        ownerAddress = yield this.getUsernameOwner(username);
                        if (ownerAddress !== constants_1.NULL_ADDRESS) {
                            throw new Error(`ENS: Username ${username} is not available`);
                        }
                    }
                    if (ownerAddress === constants_1.NULL_ADDRESS) {
                        yield (0, tx_1.waitRequestTransaction)(this._provider, registerRequest, () => this._fdsRegistrarContract.register(keccak256(toUtf8Bytes(username)), address, expires));
                    }
                    registerRequest.stage = RegisterUsernameStage.FDS_REGISTER_COMPLETED;
                }
                if (registerRequest.stage < RegisterUsernameStage.SET_RESOLVER_COMPLETED) {
                    yield (0, tx_1.waitRequestTransaction)(this._provider, registerRequest, () => this._ensRegistryContract.setResolver(this.hashUsername(username), this._publicResolverContract.address));
                    registerRequest.stage = RegisterUsernameStage.SET_RESOLVER_COMPLETED;
                }
                if (registerRequest.stage < RegisterUsernameStage.SET_PUBLIC_KEY_COMPLETED) {
                    yield this.setNameAndPublicKey(registerRequest, username, address, publicKey);
                    registerRequest.stage = RegisterUsernameStage.SET_PUBLIC_KEY_COMPLETED;
                }
                if (registerRequest.stage < RegisterUsernameStage.SET_NAME_COMPLETED) {
                    yield (0, tx_1.waitRequestTransaction)(this._provider, registerRequest, () => this._reverseRegistrarContract.setName(username));
                    registerRequest.stage = RegisterUsernameStage.SET_NAME_COMPLETED;
                }
            }
            catch (error) {
                if ((0, blockchain_1.isTxError)(error)) {
                    throw new Error((0, blockchain_1.extractMessageFromFailedTx)(error));
                }
                throw error;
            }
        });
    }
    /**
     * Retrieves the estimated gas usage for all methods involved in username registration
     * @returns {number} gas amount estimation
     */
    registerUsernameApproximateGas() {
        return this.config.gasEstimation;
    }
    /**
     * Calculates total price for username registration
     * @param priorityPrice an additional fee for the transaction, defaults to 1.5 gwei
     * @returns {BigNumber} approximate total price for transactions in wei
     */
    registerUsernameApproximatePrice(priorityPrice = ethers_1.utils.parseUnits('1.5', 'gwei')) {
        return __awaiter(this, void 0, void 0, function* () {
            const gas = this.registerUsernameApproximateGas();
            const gasPrice = yield this._provider.getGasPrice();
            return ethers_1.BigNumber.from(gas).mul(gasPrice.add(priorityPrice));
        });
    }
    /**
     * Estimates gas amount for the registerUsername method
     * @param username ENS username
     * @param address Owner address of the username
     * @param publicKey Hex string of a public key
     * @param expires Time in seconds
     * @param customRpc (optional) custom RPC provider if the default one can't calculate gas
     * @returns gas amount estimation
     */
    registerUsernameEstimateGas(username, address, publicKey, expires = 86400, customRpc) {
        return __awaiter(this, void 0, void 0, function* () {
            const rpc = customRpc || this._provider;
            const [publicKeyX, publicKeyY] = (0, keys_1.splitPublicKey)(publicKey);
            const encodedRegisterFn = this._fdsRegistrarContract.interface.encodeFunctionData('register', [
                keccak256(toUtf8Bytes(username)),
                address,
                expires,
            ]);
            const encodedSetResolverFn = this._ensRegistryContract.interface.encodeFunctionData('setResolver', [
                this.hashUsername(username),
                this._publicResolverContract.address,
            ]);
            const encodedSetPubkeyFn = this._publicResolverContract.interface.encodeFunctionData('setPubkey', [
                this.hashUsername(username),
                publicKeyX,
                publicKeyY,
            ]);
            const gasAmounts = yield Promise.all([
                rpc.estimateGas({
                    to: this._fdsRegistrarContract.address,
                    data: encodedRegisterFn,
                }),
                rpc.estimateGas({
                    to: this._ensRegistryContract.address,
                    data: encodedSetResolverFn,
                }),
                rpc.estimateGas({
                    to: this._publicResolverContract.address,
                    data: encodedSetPubkeyFn,
                }),
            ]);
            return gasAmounts.reduce((sum, amount) => sum.add(amount), ethers_1.BigNumber.from(0));
        });
    }
    /**
     * Returns public key of registered username
     * @param username
     * @returns public key
     */
    getPublicKey(username) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, domains_1.assertUsername)(username);
            return this.getPublicKeyByUsernameHash(this.hashUsername(username));
        });
    }
    /**
     * Returns public key of registered username
     * @param usernameHash
     * @returns public key
     */
    getPublicKeyByUsernameHash(usernameHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const [publicKeyX, publicKeyY] = yield this._publicResolverContract.pubkey(usernameHash);
            const publicKey = (0, keys_1.joinPublicKey)(publicKeyX, publicKeyY);
            if (!(0, keys_1.isPublicKeyValid)(publicKey)) {
                throw new Error('Public key is not set or is invalid');
            }
            return publicKey;
        });
    }
    /**
     * Fetches all ENS data related to the provided username
     * @param username ENS username
     * @returns All user's data stored on ENS
     */
    getUserData(username) {
        (0, domains_1.assertUsername)(username);
        return this._publicResolverContract.getAll(this.hashUsername(username));
    }
    getUsernameByAddress(address) {
        return __awaiter(this, void 0, void 0, function* () {
            const hash = yield this._nameResolverContract.name((0, address_1.hashAddress)(address));
            if (!hash || (0, keys_1.isZeroHexString)(hash)) {
                throw new Error('Address is not available in reverse registrar.');
            }
            return this._publicResolverContract.name(hash);
        });
    }
    /**
     * Sets user's public key to the user's ENS entry
     * @param request object previously created by the createRegisterUsernameRequest method
     * @param username ENS username
     * @param publicKey Public key that will be added to ENS
     */
    setNameAndPublicKey(request, username, address, publicKey) {
        (0, domains_1.assertUsername)(username);
        const [publicKeyX, publicKeyY] = (0, keys_1.splitPublicKey)(publicKey);
        return (0, tx_1.waitRequestTransaction)(this._provider, request, () => this._publicResolverContract.setAll(this.hashUsername(username), address, (0, keys_1.numberToBytes32)(ethers_1.BigNumber.from(0)), (0, keys_1.numberToBytes32)(ethers_1.BigNumber.from(0)), publicKeyX, publicKeyY, username));
    }
    hashUsername(username) {
        return namehash(`${username}.${this.domain}`);
    }
}
exports.ENS = ENS;
exports.default = ENS;
